import type { AssetDetailsResponse } from '../../api/asset/assetApiTypes';

/**
 * What the object page's photo section shows as the object's cover, and
 * whether it marks the cover as missing (PO, 2026-11-03).
 *
 * The server decides the photo by the one display rule (`bookedDisplay`,
 * `@kolula/shared`) and sends it as `shownPhoto` / `photoSource`; this only
 * reads it:
 *
 * - a ROOM's cover is its Group's photo (`Group.featuredImage`, the photo the
 *   room's edit replaces);
 * - an individually chosen object's cover is its own first photo;
 * - a pooled station unit has no cover of its own: its product's photo is the
 *   product's and shows in the Product box, never as the unit's cover.
 *
 * The marker shows where the rule's photo is null for a room or an
 * individually chosen object: that object has no cover. A room's other
 * photos do not stand in (D14 c). A pooled unit is never marked, and neither
 * is a response from an older backend that sends no `photoSource`.
 */
export const objectCover = (
    data: Pick<AssetDetailsResponse, 'Group' | 'shownPhoto' | 'photoSource'> | undefined,
    isRoomObject: boolean,
): { cover: string | null, coverMissing: boolean } => {
    if (!data) return { cover: null, coverMissing: false };
    // The server's rule decides whether this is a room (`photoSource`); the
    // page's own scope test is the fallback for an older server only.
    const isRoom = data.photoSource ? data.photoSource === 'room' : isRoomObject;
    let cover: string | null = null;
    if (isRoom) cover = data.Group?.featuredImage ?? null;
    else if (data.photoSource === 'object') cover = data.shownPhoto ?? null;
    const coverMissing = data.shownPhoto === null
        && (data.photoSource === 'room' || data.photoSource === 'object');
    return { cover, coverMissing };
};

/**
 * The Product box's name and description: the server's rule
 * (`productIdentity`). Only a response from a server that predates it (a
 * deploy in between) falls back to the raw Group, as the page did before;
 * a null `productIdentity` means the rule found no product and stays empty.
 */
export const objectProductFields = (
    data: Pick<AssetDetailsResponse, 'Group' | 'productIdentity'> | undefined,
): { productName: string | undefined, productDescription: string | undefined } => {
    if (data?.productIdentity === undefined) {
        return {
            productName: data?.Group?.name ?? undefined,
            productDescription: data?.Group?.description?.trim() || undefined,
        };
    }
    return {
        productName: data.productIdentity?.name ?? undefined,
        productDescription: data.productIdentity?.description.trim() || undefined,
    };
};

/** The marker tile shows in view mode, where no cover is shown; edit mode offers the add-cover tile instead. */
export const showsCoverMissingTile = (coverMissing: boolean, isEditing: boolean, cover: string | null): boolean =>
    coverMissing && !isEditing && !cover;
